import { UI } from "@wwimmo/ui";
import { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./DeleteInvoiceModal.module.css";
import { RestartCancelInvoiceProgress } from "src/screens/creditors/creditor/details/actions/restartInvoice/progress/RestartCancelInvoiceProgress";
import {
    RESTART_CANCEL_INVOICE_STATE,
    RESTART_CANCEL_MODAL_TYPE
} from "src/stores/krediflow/RestartCancelInvoiceStore";
import { useHistory } from "react-router";
import { Route, selectRoute } from "src/config/routes";
import { MessageType } from "src/components/notifications/Notifier";

const DeleteInvoiceModalBase = () => {
    const { t } = useTranslation();
    const { restartCancelInvoiceStore, invoiceListStore, invoiceStore, authStore, uiStore } =
        useContext(RootStoreContext);
    const history = useHistory();

    const toggleDisplayDeleteInvoiceModal = useCallback(() => {
        const modalIsBeingClosed = restartCancelInvoiceStore.isModalDisplayed;

        restartCancelInvoiceStore.setIsModalDisplayed(!restartCancelInvoiceStore.isModalDisplayed);

        if (modalIsBeingClosed) {
            if (
                restartCancelInvoiceStore.restartCancelInvoiceState === RESTART_CANCEL_INVOICE_STATE.FINISHED &&
                invoiceListStore.isBatchProcessing
            ) {
                invoiceListStore.navigateToNextBatchProcess();
            }

            restartCancelInvoiceStore.reset();
        }
    }, [restartCancelInvoiceStore, invoiceListStore]);

    const onClickDelete = useCallback(async () => {
        const currentInvoiceId = invoiceStore.currentInvoice?.id;
        const currentWorkflowId = invoiceStore.currentInvoice?.workflowInstance;

        if (currentInvoiceId && currentWorkflowId) {
            const wasInvoiceSuccessfullyCanceled = await restartCancelInvoiceStore.cancelInvoice(
                currentInvoiceId,
                currentWorkflowId,
                true
            );

            if (wasInvoiceSuccessfullyCanceled) {
                restartCancelInvoiceStore.reset();
                uiStore.printStatusMessage(t("screens.kredi_flow.action.delete_invoice.success"), MessageType.SUCCESS);
                history.replace(selectRoute(Route.creditors, authStore.user?.role));
            }
        }
    }, [restartCancelInvoiceStore, invoiceStore, history, authStore]);

    return (
        <UI.Modal
            title={t("screens.kredi_flow.action.delete").toString()}
            size="lg"
            show={
                restartCancelInvoiceStore.isModalDisplayed &&
                restartCancelInvoiceStore.displayedModalType === RESTART_CANCEL_MODAL_TYPE.DELETE
            }
            onClose={toggleDisplayDeleteInvoiceModal}
            centered={true}
        >
            {restartCancelInvoiceStore.restartCancelInvoiceState === RESTART_CANCEL_INVOICE_STATE.INIT ? (
                <>
                    <UI.Row className={"mt-2"}>
                        <UI.Col sm={12}>
                            <div className="mt-2 mb-4">
                                {t("screens.kredi_flow.action.delete_invoice.confirm_question")}
                            </div>
                        </UI.Col>
                    </UI.Row>
                    <UI.Row>
                        <UI.Col>
                            <UI.Button
                                label={t("labels.cancel").toString()}
                                className={`mb-2 ${styles.CancelButton}`}
                                onClick={toggleDisplayDeleteInvoiceModal}
                                variant="outline-primary"
                            />
                        </UI.Col>
                        <UI.Col>
                            <UI.Button
                                label={t("screens.kredi_flow.action.delete").toString()}
                                className={`mb-2 ml-auto ${styles.SubmitButton}`}
                                onClick={onClickDelete}
                            />
                        </UI.Col>
                    </UI.Row>
                </>
            ) : undefined}

            {restartCancelInvoiceStore.restartCancelInvoiceState === RESTART_CANCEL_INVOICE_STATE.RESTARTING ||
            restartCancelInvoiceStore.restartCancelInvoiceState === RESTART_CANCEL_INVOICE_STATE.CANCELLING ||
            restartCancelInvoiceStore.restartCancelInvoiceState === RESTART_CANCEL_INVOICE_STATE.FINISHED ? (
                <RestartCancelInvoiceProgress
                    key={"approve-reject-invoice-progress-assigning"}
                    currentRestartInvoiceState={restartCancelInvoiceStore.restartCancelInvoiceState}
                />
            ) : undefined}
        </UI.Modal>
    );
};

export const DeleteInvoiceModal = Sentry.withProfiler(observer(DeleteInvoiceModalBase));
